import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { memo } from 'preact/compat';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';
import { useIsMobile } from '@/hooks/useIsMobile';
const dictToChartData = (dict, topN = 8, includeOther = false) => {
    if (!dict)
        return [];
    const entries = Object.entries(dict).sort((a, b) => (b[1] || 0) - (a[1] || 0));
    const top = entries.slice(0, topN);
    const rest = entries.slice(topN);
    const data = top.map(([label, count]) => ({ label, count }));
    if (includeOther && rest.length > 0) {
        const otherTotal = rest.reduce((sum, [, c]) => sum + (c || 0), 0);
        data.push({ label: 'Other', count: otherTotal });
    }
    return data;
};
// Custom order for company sizes
const COMPANY_SIZE_ORDER = [
    'Unknown',
    '1-10',
    '11-50',
    '51-200',
    '201-500',
    '501-1000',
    '1001-5000',
    '5001-10000',
    '10001+',
];
const normalizeCompanySizeLabel = (label) => {
    const s = (label || '').toString().trim().toLowerCase().replace(/,/g, '');
    if (!s || s === '-' || s === 'unknown' || s === 'n/a' || s === 'na' || s.includes('unknown'))
        return 'Unknown';
    // Plus variants (e.g., 10000+, 10001+, 10001 and above)
    if (/^\d+\s*\+$/.test(s) || /\b\d{4,}\s*(\+|and above|or more)\b/.test(s)) {
        const num = parseInt(s, 10);
        if (!isNaN(num) && num >= 10001)
            return '10001+';
    }
    // Range variants (e.g., 2-10, 10-50, 5,001-10,000)
    const m = s.match(/(\d+)\s*[-–]\s*(\d+)/);
    if (m) {
        const max = parseInt(m[2], 10);
        if (!isNaN(max)) {
            if (max <= 10)
                return '1-10';
            if (max <= 50)
                return '11-50';
            if (max <= 200)
                return '51-200';
            if (max <= 500)
                return '201-500';
            if (max <= 1000)
                return '501-1000';
            if (max <= 5000)
                return '1001-5000';
            if (max <= 10000)
                return '5001-10000';
            if (max > 10000)
                return '10001+';
        }
    }
    // Exact textual matches
    const map = {
        '1-10': '1-10',
        '11-50': '11-50',
        '51-200': '51-200',
        '201-500': '201-500',
        '501-1000': '501-1000',
        '1001-5000': '1001-5000',
        '5001-10000': '5001-10000',
        '10001+': '10001+',
        '10000+': '10001+',
        '10001 and above': '10001+',
    };
    if (map[s])
        return map[s];
    return 'Unknown';
};
const companySizesToOrderedData = (dict) => {
    if (!dict)
        return [];
    const bucketCounts = Object.fromEntries(COMPANY_SIZE_ORDER.map(k => [k, 0]));
    for (const [rawLabel, count] of Object.entries(dict)) {
        const normalized = normalizeCompanySizeLabel(rawLabel);
        bucketCounts[normalized] = (bucketCounts[normalized] || 0) + (count || 0);
    }
    const ordered = [];
    for (const label of COMPANY_SIZE_ORDER) {
        const c = bucketCounts[label];
        if (c && c > 0)
            ordered.push({ label, count: c });
    }
    return ordered;
};
const BarCard = ({ title, data, palette, theme }) => (_jsxs("div", { className: "bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4", children: [_jsx("h3", { className: "text-sm font-medium text-slate-800 dark:text-slate-200 mb-3", children: title }), _jsx("div", { style: { height: 240 }, children: _jsx(ResponsiveBar, { data: data, keys: ["count"], indexBy: "label", margin: { top: 10, right: 10, bottom: 60, left: 50 }, padding: 0.3, colorBy: "indexValue", colors: palette, axisBottom: { tickRotation: 45 }, enableLabel: false, theme: theme }) })] }));
const HBarCard = ({ title, data, palette, theme, isMobile }) => (_jsxs("div", { className: "bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4", children: [_jsx("h3", { className: "text-sm font-medium text-slate-800 dark:text-slate-200 mb-3", children: title }), _jsx("div", { style: { height: 240 }, children: _jsx(ResponsiveBar, { data: data, keys: ["count"], indexBy: "label", layout: "horizontal", margin: isMobile ? { top: 10, right: 10, bottom: 30, left: 80 } : { top: 10, right: 20, bottom: 30, left: 120 }, padding: 0.3, colorBy: "indexValue", colors: palette, axisBottom: { tickRotation: 0 }, enableLabel: false, theme: theme }) })] }));
export const CampaignListCharts = memo(({ analytics, palette, nivoTheme }) => {
    const isMobile = useIsMobile();
    return (_jsxs("div", { className: "grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6", children: [_jsxs("div", { className: "grid grid-rows-2 gap-6", children: [_jsx(HBarCard, { title: "Seniority", data: dictToChartData(analytics.Seniority, 8, true), palette: palette, theme: nivoTheme, isMobile: isMobile }), _jsx(BarCard, { title: "Company Sizes", data: companySizesToOrderedData(analytics.CompanySizes), palette: palette, theme: nivoTheme })] }), _jsx("div", { className: "lg:row-span-2", children: _jsxs("div", { className: "bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 h-full", children: [_jsx("h3", { className: "text-sm font-medium text-slate-800 dark:text-slate-200 mb-3", children: "Industries" }), _jsx("div", { style: { height: isMobile ? 320 : 'calc(100% - 2rem)' }, children: _jsx(ResponsivePie, { data: dictToChartData(analytics.Industries, 10, true).map(d => ({ id: d.label, label: d.label, value: d.count })), colors: palette, margin: isMobile ? { top: 20, right: 20, bottom: 20, left: 20 } : { top: 60, right: 140, bottom: 60, left: 140 }, innerRadius: 0.5, padAngle: 0.7, cornerRadius: 3, activeOuterRadiusOffset: 8, enableArcLinkLabels: !isMobile, arcLinkLabelsSkipAngle: 10, arcLinkLabelsTextColor: { from: 'color' }, arcLinkLabelsThickness: 2, arcLinkLabelsColor: { from: 'color' }, arcLinkLabelsDiagonalLength: 16, arcLinkLabelsStraightLength: 24, arcLinkLabelsTextOffset: 6, enableArcLabels: false, theme: nivoTheme }) })] }) })] }));
});
